import React from "react";
import PropTypes from "prop-types";
import { Button, Plus, Select } from "@opendate-app/setlist";
import {
  ADJUSTMENT_KINDS,
  MAX_ADJUSTMENTS,
  adjustPrice,
  adjustmentAmountProblem,
  adjustmentLimit,
  formatEntryTime,
  formatMoney,
  formatTimes,
  usableAdjustment,
} from "../ticketSets";

let adjustmentSequence = 0;
export const blankPriceAdjustment = () => {
  adjustmentSequence += 1;
  return { id: `series-price-adjustment-${adjustmentSequence}`, label: "", kind: "less_amt", value: "", times: [] };
};

const toggled = (values, value) => (values.includes(value) ? values.filter((each) => each !== value) : [...values, value].sort());

const TimeBasedPricing = ({ adjustments, times, samplePaid, locked, onChange }) => {
  const update = (id, patch) => onChange(adjustments.map((adjustment) => (adjustment.id === id ? { ...adjustment, ...patch } : adjustment)));

  return (
    <div className="series-set-pricing">
      <div className="series-set-pricing-header">
        <div>
          <div className="series-set-subheading">Time-based Pricing</div>
          <p className="series-edit-hint">
            Change prices for certain entry times, like cheaper early entry or a discount for slower hours. Free tickets
            aren&apos;t affected.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={Plus}
          disabled={adjustments.length >= MAX_ADJUSTMENTS}
          onClick={() => onChange([...adjustments, blankPriceAdjustment()])}
        >
          Add Price Adjustment
        </Button>
      </div>

      {adjustments.map((adjustment, index) => {
        const percent = (adjustment.kind || "").endsWith("pct");
        const amountProblem = adjustmentAmountProblem(adjustment);
        const ready = samplePaid && (adjustment.times || []).length > 0 && usableAdjustment(adjustment);
        return (
          <div key={adjustment.id} className="series-set-adjustment" data-testid="price-adjustment">
            <div className="series-set-adjustment-fields">
              <label className="series-show-field">
                <span className="series-edit-label series-edit-label-flush">Label</span>
                <input
                  className="series-edit-input"
                  aria-label={`Price adjustment ${index + 1} label`}
                  placeholder="e.g., Early entry"
                  value={adjustment.label || ""}
                  onChange={(event) => update(adjustment.id, { label: event.target.value })}
                />
              </label>
              <div className="series-show-field">
                <Select
                  id={`${adjustment.id}-kind`}
                  label="Adjustment"
                  size="sm"
                  options={ADJUSTMENT_KINDS}
                  value={adjustment.kind}
                  disabled={Boolean(locked)}
                  onChange={(kind) => kind && update(adjustment.id, { kind })}
                />
              </div>
              <label className="series-show-field series-set-adjustment-amount">
                <span className="series-edit-label series-edit-label-flush">{percent ? "Amount (%)" : "Amount ($)"}</span>
                <input
                  type="number"
                  min="0"
                  max={adjustmentLimit(adjustment.kind)}
                  step="any"
                  className="series-edit-input"
                  aria-label={`Price adjustment ${index + 1} amount`}
                  placeholder="0"
                  value={adjustment.value}
                  onChange={(event) => update(adjustment.id, { value: event.target.value })}
                />
                {amountProblem && <span className="series-schedule-error">{amountProblem}</span>}
              </label>
              <Button
                variant="link-gray"
                size="sm"
                aria-label={`Remove price adjustment ${index + 1}`}
                className="series-row-action-destructive"
                onClick={() => onChange(adjustments.filter((other) => other.id !== adjustment.id))}
              >
                Remove
              </Button>
            </div>

            <span className="series-edit-label">Applies to Entry Times</span>
            {times.length > 0 ? (
              <div className="series-weekday-row">
                {times.map((time) => {
                  const active = (adjustment.times || []).includes(time);
                  return (
                    <button
                      key={time}
                      type="button"
                      aria-pressed={active}
                      className={["series-weekday", active ? "selected" : ""].join(" ").trim()}
                      onClick={() => update(adjustment.id, { times: toggled(adjustment.times || [], time) })}
                    >
                      {formatEntryTime(time)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="series-edit-hint">Generate a schedule to choose entry times.</p>
            )}

            {ready && (
              <p className="series-set-adjustment-preview" data-testid="price-adjustment-preview">
                {samplePaid.name}: {formatMoney(samplePaid.price)} {"→"}{" "}
                <strong>{formatMoney(adjustPrice(samplePaid.price, adjustment))}</strong> at {formatTimes(adjustment.times)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

TimeBasedPricing.propTypes = {
  adjustments: PropTypes.arrayOf(PropTypes.object).isRequired,
  times: PropTypes.arrayOf(PropTypes.string).isRequired,
  samplePaid: PropTypes.object,
  locked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default TimeBasedPricing;
